'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQ {
  question: string;
  answer: string;
}

const FAQSection = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const faqs: FAQ[] = [
    {
      question: 'What is the typical response time for consultation requests?',
      answer: 'We respond to all consultation requests within 24 hours during business days. For urgent matters, our expert call service provides same-day responses.'
    },
    {
      question: 'Do you offer free initial consultations?',
      answer: 'Yes, we provide complimentary 30-minute discovery calls to understand your needs and determine how we can best support your technology initiatives.'
    },
    {
      question: 'What information should I prepare before scheduling a consultation?',
      answer: 'Having a brief overview of your project scope, timeline expectations, and key challenges helps us match you with the right expert and make the most of your consultation time.'
    },
    {
      question: 'Can I schedule consultations outside regular business hours?',
      answer: 'Yes, we accommodate different time zones and schedules. During the booking process, you can select from available time slots that work best for you, including early morning and evening options.'
    },
    {
      question: 'What happens after I submit a contact form?',
      answer: 'You\'ll receive an automated confirmation email immediately, followed by a personalized response from our team within 24 hours. We\'ll schedule a call or meeting based on your preferences and availability.'
    },
    {
      question: 'Do you work with companies outside the United States?',
      answer: 'Absolutely. We serve clients globally and have experience working across different time zones. Our Microsoft and AWS partnerships enable us to deliver solutions worldwide.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-3/4 mx-auto" />
            <div className="h-4 bg-muted rounded w-1/2 mx-auto" />
            <div className="space-y-3 mt-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-muted rounded" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Quick answers to common questions about contacting us
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-card rounded-lg border border-border shadow-sm overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors duration-base"
              >
                <span className="text-lg font-headline font-semibold text-foreground pr-4">
                  {faq.question}
                </span>
                <Icon
                  name="ChevronDownIcon"
                  size={24}
                  variant="outline"
                  className={`flex-shrink-0 text-muted-foreground transition-transform duration-base ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-4 pt-2">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;